"use client";

import React from "react";
import { Check, X, ShieldCheck, Info } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheSecurityBenchmark() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const features = [{
    name: "Visa & Mastercard",
    description: "Global credit and debit card processing with advanced fraud protection.",
    securitas: true,
    standard: true
  }, {
    name: "Bank Wire Transfer",
    description: "Direct institutional bank-to-bank transfer for high-volume traders.",
    securitas: false,
    standard: true
  }, {
    name: "PayPal & E-Wallets",
    description: "Instant digital wallet integration for seamless capital movement.",
    securitas: true,
    standard: true
  }, {
    name: "USDT / Crypto Assets",
    description: "Blockchain-verified digital asset funding with sub-30min execution.",
    securitas: true,
    standard: true
  }, {
    name: "Skrill / Neteller",
    description: "Optimized e-payment solutions for global financial agility.",
    securitas: true,
    standard: true
  }, {
    name: "Apple & Google Pay",
    description: "One-tap mobile authentication for secure, rapid account funding.",
    securitas: true,
    standard: true
  }, {
    name: "SEPA Transfer",
    description: "Standardized European payment protocol for cross-border reliability.",
    securitas: false,
    standard: true
  }, {
    name: "Institutional API",
    description: "Automated funding protocols for high-frequency trading accounts.",
    securitas: true,
    standard: true
  }];
  return <section data-section-id="49157" id="deposit-withdrawal-methods" ref={ref} className={`py-16 dark bg-background text-foreground transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-foreground mb-4">
            Deposit & Withdrawal Methods
          </h2>
          <p className="text-lg text-muted-foreground font-medium">
            TradeSphere provides institutional-grade gateways for high-speed capital movement. 
            Experience zero-fee transactions with the agility of high-frequency trading.
          </p>
        </div>

        <div className="relative rounded-sm border border-border shadow-md overflow-hidden bg-card">
          <Table className="w-full">
            <TableHeader className="bg-secondary/20">
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="w-[40%] min-w-[200px] py-6 px-6 text-base font-black text-foreground sticky top-0 bg-secondary/20 z-10 backdrop-blur-md">
                  Funding Method
                </TableHead>
                <TableHead className="w-[30%] min-w-[160px] py-6 px-6 text-center sticky top-0 bg-primary/5 z-10 backdrop-blur-md border-x border-primary/10">
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-lg font-black text-primary tracking-tight">
                      Instant Execution
                    </span>
                    <span className="text-xs font-bold text-primary-foreground bg-primary px-2 py-0.5 rounded-sm">
                      Recommended
                    </span>
                  </div>
                </TableHead>
                <TableHead className="w-[30%] min-w-[160px] py-6 px-6 text-center text-base font-black text-muted-foreground sticky top-0 bg-secondary/20 z-10 backdrop-blur-md">
                  Zero Commissions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {features.map((feature, i) => <TableRow key={i} data-index={i} className="hover:bg-secondary/10 border-b border-border last:border-0">
                  <TableCell className="py-4 px-6 align-top">
                    <div className="flex flex-col gap-1">
                      <span className="font-bold text-foreground flex items-center gap-2">
                        {feature.name}
                        <div className="group relative">
                          <Info className="w-4 h-4 text-primary/50 cursor-help" />
                          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-popover text-popover-foreground text-xs rounded-sm shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 border border-border">
                            {feature.description}
                          </div>
                        </div>
                      </span>
                      <span className="text-sm text-muted-foreground md:hidden font-mono">
                        {feature.description}
                      </span>
                    </div>
                  </TableCell>
                  
                  {}
                  <TableCell className="py-4 px-6 text-center align-middle bg-primary/5 border-x border-primary/10">
                    <div className="flex justify-center items-center h-full">
                      {feature.securitas ? <div className="flex items-center justify-center w-8 h-8 rounded-sm bg-primary/20">
                          <Check className="w-5 h-5 text-primary" />
                        </div> : <X className="w-6 h-6 text-muted-foreground/30" />}
                    </div>
                  </TableCell>

                  {}
                  <TableCell className="py-4 px-6 text-center align-middle">
                    <div className="flex justify-center items-center h-full">
                      {feature.standard ? <div className="flex items-center justify-center w-8 h-8 rounded-sm bg-muted">
                          <Check className="w-5 h-5 text-muted-foreground" />
                        </div> : <X className="w-6 h-6 text-destructive/40" />}
                    </div>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>

        <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-6 bg-secondary/20 p-6 rounded-sm border border-border">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-sm shrink-0">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-black text-lg text-foreground tracking-tight">
                Institutional Liquidity Access
              </h3>
              <p className="text-muted-foreground text-sm max-w-md font-medium">
                Manage your portfolio with professional-grade tools. Deposit funds and execute trades with zero latency.
              </p>
            </div>
          </div>
          <div className="flex gap-4 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto rounded-sm font-black shadow-lg shadow-primary/20 bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/FundingPage">
                Deposit Now
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full md:w-auto rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-primary font-bold">
              <Link to="/AcademyPage">
                Learn Trading
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}